/**
 * シフトパターンタグAPI クライアント
 */
import { parseApiError } from "../utils/apiError";
import { authFetch, API_BASE_URL } from "./client";

export interface ShiftPatternTagResponse {
    id: number;
    organization_id: number | null;
    name: string | null;
    description: string | null;
    color: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface ShiftPatternTagListResponse {
    items: ShiftPatternTagResponse[];
    total: number;
}

export interface ShiftPatternTagCreateRequest {
    organization_id?: number | null;
    name?: string | null;
    description?: string | null;
    color?: string | null;
}

export interface ShiftPatternTagUpdateRequest {
    organization_id?: number | null;
    name?: string | null;
    description?: string | null;
    color?: string | null;
}

export interface ShiftPatternTagAssignmentResponse {
    shift_pattern_id: number;
    tag_ids: number[];
}

/** シフトパターンタグマスタ一覧 */
export async function fetchShiftPatternTags(): Promise<ShiftPatternTagListResponse> {
    const response = await authFetch(`${API_BASE_URL}/shift-pattern-tags`);
    if (!response.ok) {
        throw new Error(`Failed to fetch shift pattern tags: ${response.status}`);
    }
    return response.json();
}

/** シフトパターンタグを作成 */
export async function createShiftPatternTag(
    data: ShiftPatternTagCreateRequest,
): Promise<ShiftPatternTagResponse> {
    const response = await authFetch(`${API_BASE_URL}/shift-pattern-tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw await parseApiError(response, "タグの作成に失敗しました");
    }
    return response.json();
}

/** シフトパターンタグを更新 */
export async function updateShiftPatternTag(
    tagId: number,
    data: ShiftPatternTagUpdateRequest,
): Promise<ShiftPatternTagResponse> {
    const response = await authFetch(
        `${API_BASE_URL}/shift-pattern-tags/${tagId}`,
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        },
    );
    if (!response.ok) {
        throw await parseApiError(response, "タグの更新に失敗しました");
    }
    return response.json();
}

/** シフトパターンタグを削除（論理削除） */
export async function deleteShiftPatternTag(tagId: number): Promise<void> {
    const response = await authFetch(
        `${API_BASE_URL}/shift-pattern-tags/${tagId}`,
        { method: "DELETE" },
    );
    if (!response.ok) {
        throw await parseApiError(response, "タグの削除に失敗しました");
    }
}

/** シフトパターンに紐づくタグIDリストを取得 */
export async function fetchShiftPatternTagAssignments(
    patternId: number,
): Promise<ShiftPatternTagAssignmentResponse> {
    const response = await authFetch(
        `${API_BASE_URL}/shift-patterns/${patternId}/tags`,
    );
    if (!response.ok) {
        throw new Error(`Failed to fetch shift pattern tag assignments: ${response.status}`);
    }
    return response.json();
}

/** シフトパターンのタグ紐づけを一括更新 */
export async function updateShiftPatternTagAssignments(
    patternId: number,
    tagIds: number[],
): Promise<ShiftPatternTagAssignmentResponse> {
    const response = await authFetch(
        `${API_BASE_URL}/shift-patterns/${patternId}/tags`,
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tag_ids: tagIds }),
        },
    );
    if (!response.ok) {
        throw await parseApiError(response, "タグの紐づけ更新に失敗しました");
    }
    return response.json();
}
